import { closeBrackets, closeBracketsKeymap, autocompletion, acceptCompletion } from "@codemirror/autocomplete";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import {
    bracketMatching,
    foldGutter,
    indentOnInput,
    indentUnit,
    LanguageDescription,
    syntaxHighlighting,
} from "@codemirror/language";
import { languages } from "@codemirror/language-data";
import {
    drawSelection,
    dropCursor,
    EditorView,
    highlightActiveLine,
    highlightActiveLineGutter,
    highlightSpecialChars,
    KeyBinding,
    keymap,
    lineNumbers,
} from "@codemirror/view";
import { EditorState, Extension } from "@codemirror/state";
import { I18n } from "@/i18n/i18n";
import { rougeStyle } from "@/rouge_style";
import { additionalLanguages } from "@/editor_languages";

const tabCompletionKeyMap: KeyBinding = { key: "Tab", run: acceptCompletion };
const submitKeyMap: KeyBinding = {
    key: "Mod-Enter",
    run: () => {
        // A disabled button ignores click(), so submitting while judging stays blocked.
        document.querySelector<HTMLButtonElement>("d-handin-button button")?.click();
        return true;
    },
};

// Basic, built-in extensions.
const editorSetup = (() => [
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightSpecialChars(),
    history(),
    foldGutter(),
    drawSelection(),
    dropCursor(),
    indentUnit.of("    "),
    indentOnInput(),
    bracketMatching(),
    closeBrackets(),
    highlightActiveLine(),
    EditorState.allowMultipleSelections.of(true),
    keymap.of([
        submitKeyMap,
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        tabCompletionKeyMap,
        indentWithTab,
    ]),
    syntaxHighlighting(rougeStyle, {
        fallback: true,
    }),
    autocompletion(),
])();

async function loadProgrammingLanguage(language: string): Promise<Extension | undefined> {
    const potentialLanguages = additionalLanguages.concat(languages);
    const description = LanguageDescription.matchLanguageName(potentialLanguages, language);
    if (description) {
        await description.load();
        return description.support;
    }
    console.warn(`${language} is not supported by our editor, falling back to nothing.`);
}

const ESCAPE_HINT_ID = "editor-escape-hint";

let lastInputWasKeyboard = false;
document.addEventListener("pointerdown", () => (lastInputWasKeyboard = false), true);
document.addEventListener("keydown", () => (lastInputWasKeyboard = true), true);

/**
 * Shows the visible escape hint only while the editor has keyboard-initiated focus.
 * `:focus-visible` always matches a contenteditable, mouse or not, so it cannot tell the two apart.
 */
function escapeHintVisibility(parent: Element): Extension {
    return EditorView.domEventObservers({
        focus: () => parent.classList.toggle("show-escape-hint", lastInputWasKeyboard),
        blur: () => parent.classList.remove("show-escape-hint"),
    });
}

function appendEscapeHints(parent: Element): void {
    const description = document.createElement("span");
    description.className = "visually-hidden";
    description.id = ESCAPE_HINT_ID;
    description.textContent = I18n.t("js.handin.escape_hint_description");
    const hint = document.createElement("span");
    hint.className = "editor-escape-hint";
    hint.setAttribute("aria-hidden", "true");
    hint.textContent = I18n.t("js.handin.escape_hint");
    parent.append(description, hint);
}

/**
 * Set up the code editor.
 *
 * @param parent The element to insert the editor into. Existing content will be inserted into the editor.
 * @param programmingLanguage The programming language of the editor. Will attempt to load language support.
 * @param readOnly Whether the editor is read-only.
 */
export async function configureEditor(
    parent: Element,
    programmingLanguage: string,
    readOnly = false,
): Promise<EditorView> {
    const existingCode = parent.textContent;
    // Clear the existing code, as we will put it in CodeMirror.
    parent.textContent = "";
    const languageSupport = await loadProgrammingLanguage(programmingLanguage);
    const languageExtensions = [];
    if (languageSupport !== undefined) {
        languageExtensions.push(languageSupport);
    }
    const view = new EditorView({
        doc: existingCode,
        extensions: [
            // Basic editor functionality
            editorSetup,
            // Language support
            ...languageExtensions,
            // Accessible name for the editable region
            EditorView.contentAttributes.of({
                "aria-label": I18n.t("js.handin.editor"),
                ...(readOnly ? {} : { "aria-describedby": ESCAPE_HINT_ID }),
            }),
            ...(readOnly ? [] : [escapeHintVisibility(parent)]),
            ...(readOnly ? [EditorView.editable.of(false)] : []),
        ],
        parent: parent,
    });
    if (!readOnly) {
        appendEscapeHints(parent);
    }
    return view;
}

/**
 * Set the content of a code editor.
 *
 * @param editorView The code editor to set the content in.
 * @param code The code to insert.
 */
export function setCode(editorView: EditorView, code: string): void {
    editorView.dispatch(
        editorView.state.update({
            changes: {
                from: 0,
                to: editorView.state.doc.length,
                insert: code,
            },
            selection: { anchor: 0 },
        }),
    );
}
